import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Form from "../../../components/Form/Form";
import FormTitle from "../../../components/FormTitle/FormTitle";
import Input from "../../../components/Input/Input";
import Loader from "../../../components/Loader/Loader";
import UnitService from "../../../services/UnitService";
import "./FormUnits.css";
import { useAuth } from "../../../contexts/auth";
import { useUserStore } from "../../../contexts/userStore";
import { MdOutlineMapsHomeWork } from "react-icons/md";
import { DynamicIcon } from "../../../components/DynamicIcon/DynamicIcon";
import Select from "../../../components/Select/Select";
import { ParkingLotControlTypeText } from "../../../enums/parkingLotTypes";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import { DynamicTagFields } from "../../../components/DynamicTagFields/DynamicTagFields";
import { UnitSelector } from "../../../components/UnitSelector/UnitSelector";

function FormUnits(props) {
	const { user } = useAuth();
	const [name, setName] = useState(props.unit?.name);
	const [customInput1, setCustomInput1] = useState(props.unit?.customInput1);
	const [customInput2, setCustomInput2] = useState(props.unit?.customInput2);
	const [customInput3, setCustomInput3] = useState(props.unit?.customInput3);
	const [metadata, setMetadata] = useState(props.unit?.metadata || []);
	const [uuid, setUuid] = useState(props.unit?.externalId);
	const [showLoader, setShowLoader] = useState(false);
	const { selectedCondominio } = useUserStore();
	const [parentId, setParentId] = useState(selectedCondominio);

	useEffect(() => {
		setParentId(props.unit?.parentId ?? selectedCondominio);
	}, [props.unit, selectedCondominio]);

	useEffect(() => {
		setName(props.unit?.name);
		setCustomInput1(props.unit?.customInput1);
		setCustomInput2(props.unit?.customInput2);
		setCustomInput3(props.unit?.customInput3);
		setMetadata(props.unit?.metadata || []);
		setUuid(props.unit?.externalId);
	}, [props.unit, props.unitsConfigs]);

	const navigate = useNavigate();

	const buildPayload = () => {
		const parkingLotQuantityProps = props.unit?.metadata?.find(item => item.name === 'parkingLotQuantity');
		const parkingLotBusyProps = props.unit?.metadata?.find(item => item.name === 'parkingLotBusy');
		const customInput1Props = props.unit?.metadata?.find(item => item.name === 'customInput1');
		const customInput2Props = props.unit?.metadata?.find(item => item.name === 'customInput2');
		const customInput3Props = props.unit?.metadata?.find(item => item.name === 'customInput3');
		const tags = new Set();

		if (props.tag) {
			tags.add(props.tag?.externalId);
		}
		props.unit?.tags?.forEach(tag => tags.add(tag.externalId));

		let payload = {
			name_local: name,
			id_client: user.userData.selectedOrganizationId,
			parent_unit_id: `${parentId}`,
			is_device_unit: true,
			tags: Array.from(tags),
			metadata: [
				{
					name: 'parkingLotQuantity',
					label: parkingLotQuantityProps?.label ?? 'parkingLotQuantity',
					value: metadata.find(item => item.name === 'parkingLotQuantity')?.value ?? '',
				},
				{
					name: 'parkingLotBusy',
					label: parkingLotBusyProps?.label ?? 'parkingLotBusy',
					value: metadata.find(item => item.name === 'parkingLotBusy')?.value ?? '',
				},
				{
					name: 'customInput1',
					label: customInput1Props?.label ?? 'customInput1',
					value: customInput1,
				},
				{
					name: 'customInput2',
					label: customInput2Props?.label ?? 'customInput2',
					value: customInput2,
				},
				{
					name: 'customInput3',
					label: customInput3Props?.label ?? 'customInput3',
					value: customInput3,
				},
				...metadata.filter(item => !['parkingLotQuantity', 'parkingLotBusy', 'customInput1', 'customInput2', 'customInput3'].includes(item.name))
			]
		};
		return payload;
	};

	async function handleSubmit(event) {
		event.preventDefault();
		const payload = buildPayload();
		setShowLoader(true);
		let response = {};
		if (props.type === "edicao") {
			response = await UnitService.updateUnit(props.unit.id, payload);
		}
		if (props.type === "cadastro") {
			response = await UnitService.createUnit(payload);
		}
		setShowLoader(false);
		if (response.data.id) navigate(`../consulta-units/${props.tag?.externalId ?? props.unit?.tags?.[0]?.externalId}`, {
			state: { refresh: true }
		});
	}

	async function handleDelete() {
		if (!props.unit) return;
		setShowLoader(true);
		await UnitService.deleteUnit(props.unit.id);
		navigate(`../consulta-units/${props.tag?.externalId ?? props.unit?.tags?.[0]?.externalId}`, {
			state: { deletedId: props.unit.id },
		});
		setShowLoader(false);
	}

	const handleClickEditButton = () => {
		if (!props.unit) return;
		navigate(`/units/${props.tag?.externalId}/${props.unit.externalId}`, { state: props.unit });
	};

	function onMetadataFieldChange(name, value) {
		setMetadata(prev => {
			const existing = prev.find(item => item.name === name);
			if (existing) {
				return prev.map(item => item.name === name ? { ...item, value } : item);
			} else {
				return [...prev, { name, value }];
			}
		});
	}

	const backUrl = `../consulta-units/${props.tag?.externalId ?? props.unit?.tags?.[0]?.externalId}`;

	return (
		<React.Fragment>
			<FormTitle
				type={props.type}
				icon={props.tag?.icon ? <DynamicIcon iconName={props.tag.icon} size={20} color={'white'} /> : <MdOutlineMapsHomeWork size={20} />}
				tituloConsulta={props.unit?.customInput1 || null}
				tituloCadastro={`Cadastro de ${props.tag?.name || 'Unidade'}`}
				backRoute={backUrl}
			/>
			{showLoader ? (
				<Loader />
			) : (
				<Form
					handleSubmit={handleSubmit}
					type={props.type}
					handleClickEdit={handleClickEditButton}
					handleDelete={handleDelete}
					navigateCancelUrl={backUrl}
					deleteButNotSynqed={props.unit?.deletedAt ? true : false}
				>
					<div className={`grid-2`}>
						<Input
							name="name"
							id="name"
							labelText={'Nome'}
							value={name}
							onChange={({ target }) => setName(target.value)}
							disabled={props.type === "consulta"}
							required={true}
						/>
						<UnitSelector
							type={props.type}
							unitId={selectedCondominio}
							scopedUnitId={selectedCondominio}
							parentId={parentId}
							setParentId={setParentId}
							disabled={props.type !== "cadastro"}
						/>
					</div>
					<Input
						name="uuid"
						id="uuid"
						labelText="UUID"
						value={uuid}
						onChange={({ target }) => setUuid(target.value)}
						disabled={true}
						required={false}
					/>
					{(props.unit?.tags?.find(tag => tag.parkingLimit) || props.tag?.parkingLimit) && (
						<>
							<h3 className="bordered muted-md mt-1.5! mb-0!">Controle de vagas</h3>
							<div className="grid-2">
								<Input
									name="parkingLotQuantity"
									id="parkingLotQuantity"
									type="number"
									labelText="Número de vagas *"
									value={metadata.find(item => item.name === 'parkingLotQuantity')?.value ?? ''}
									onChange={({ target }) => {
										onMetadataFieldChange('parkingLotQuantity', target.value);
									}}
									disabled={props.type === "consulta"}
									required={true}
								/>
								<Input
									name="parkingLotBusy"
									id="parkingLotBusy"
									type="number"
									labelText="Vagas Ocupadas *"
									value={metadata.find(item => item.name === 'parkingLotBusy')?.value ?? ''}
									onChange={({ target }) => {
										onMetadataFieldChange('parkingLotBusy', target.value);
									}}
									disabled={props.type === "consulta"}
									required={true}
								/>
							</div>
						</>
					)}
					{(props.unit?.tags?.find(tag => tag.parkingControl) || props.tag?.parkingControl) && (
						<>
							<h3 className="bordered muted-md mt-1.5! mb-0!">Configuração do controle de vagas</h3>
							<div className="grid-2">
								<Input
									name="parkingLotSize"
									id="parkingLotSize"
									type="number"
									labelText="Máxima Ocupação"
									value={metadata.find(item => item.name === 'parkingLotSize')?.value ?? ''}
									onChange={({ target }) => {
										onMetadataFieldChange('parkingLotSize', target.value);
									}}
									disabled={props.type === "consulta"}
									required={true}
								/>
								<Select
									name="parkingControlType"
									id="parkingControlType"
									labelText="Tipo de Controle"
									value={metadata.find(item => item.name === 'parkingControlType')?.value ?? ''}
									onChange={({ target }) => {
										onMetadataFieldChange('parkingControlType', target.value);
									}}
									disabled={props.type === "consulta"}
									required={true}
									options={Object.entries(ParkingLotControlTypeText).map(([value, text]) => ({ value, text }))}
									tooltipText={"Tipo de Controle"}
									tooltipVisible={props.type !== "consulta"}
								/>
							</div>
						</>
					)}
					{(props.unit?.tags?.find(tag => tag.areaControl) || props.tag?.areaControl) && (
						<>
							<h3 className="bordered muted-md mt-1.5! mb-0!">Configuração do controle de área</h3>
							<div className="flex gap-3 flex-col w-full">
								<ToggleButton
									label={metadata.find(item => item.name === 'creditsEnabled')?.value ? `Créditos Habilitados` : `Créditos Desabilitados`}
									selected={metadata.find(item => item.name === 'creditsEnabled')?.value || false}
									onClick={() => {
										const current = metadata.find(item => item.name === 'creditsEnabled');
										if (current) {
											onMetadataFieldChange('creditsEnabled', !current.value);
										} else {
											onMetadataFieldChange('creditsEnabled', true);
										}
									}}
									disabled={props.type === "consulta"}
									tooltipVisible={true}
									tooltipText={
										"Habilita o controle de acesso por cŕeditos do usuário nessa área."
									}
								/>
								<Input
									name="areaCapacity"
									id="areaCapacity"
									type="number"
									labelText="Máxima Ocupação"
									value={metadata.find(item => item.name === 'areaCapacity')?.value ?? ''}
									onChange={({ target }) => {
										onMetadataFieldChange('areaCapacity', target.value);
									}}
									disabled={props.type === "consulta" || !metadata.find(item => item.name === 'creditsEnabled')?.value}
									required={true}
								/>
							</div>
						</>
					)}
					{props.type !== "consulta" && (
						<DynamicTagFields
							unit={props.unit}
							tag={props.tag}
							metadata={metadata}
							setMetadata={setMetadata}
						/>
					)}
				</Form>
			)}
		</React.Fragment>
	);
}

export default FormUnits;
